import type React from "react";
import { useState } from "react";
import useApiHook from "../../../api/Api";
import { toast } from "react-toastify";

const Delete: React.FC<{ id: number }> = ({ id }) => {
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);

  const { makeRequest: deleteReport, isLoading } = useApiHook<any>(
    `/reports/${id}`,
    {
      method: "delete",
    }
  );

  const handleDelete = async (id: number) => {
    try {
      await deleteReport();
      toast.success("Report deleted successfully");

      window.location.reload();
    } catch (error) {
      toast.error("Failed to delete report");
    }
  };

  const openConfirmation = () => {
    setShowConfirmation(true);
  };

  const closeConfirmation = () => {
    setShowConfirmation(false);
  };

  const confirmDelete = () => {
    handleDelete(id);
    closeConfirmation();
  };

  return (
    <div className="relative">
      <button
        onClick={openConfirmation}
        disabled={isLoading}
        className={`
          px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 shadow-sm
          flex items-center justify-center min-w-[80px]
          ${
            isLoading
              ? "bg-red-400 cursor-not-allowed opacity-75"
              : "bg-red-500 hover:bg-red-600 text-white"
          }
        `}
      >
        {isLoading ? (
          <>
            <svg
              className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <span>Deleting...</span>
          </>
        ) : (
          "Delete"
        )}
      </button>

      {/* Confirmation Dialog */}
      {showConfirmation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={closeConfirmation}
          ></div>

          {/* Dialog */}
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6 z-10">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <svg
                  className="h-6 w-6 text-red-600"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Delete Report
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                Are you sure you want to delete this report? This action cannot
                be undone.
              </p>
            </div>

            <div className="flex justify-center space-x-4">
              <button
                onClick={closeConfirmation}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Delete;
